import { yupResolver } from '@hookform/resolvers/yup';
import { Input, IntlPhoneInput } from '@insify/design-system/components';
import { textSm } from '@insify/design-system/typography';
import clsx from 'clsx';
import defaults from 'lodash.defaults';
import { Controller, useForm } from 'react-hook-form';

import { WizardLayoutFormWithCommonSection } from '../../components/WizardLayoutForm';

import { validation } from './validation';

import type { HighCareFormType, HighCareProps } from './types';
import type { FC } from 'react';

export const HighCare: FC<HighCareProps> = ({
  content,
  disableCompanyName,
  formData,
  translations,
  eventHandlers,
  submitting,
  defaultCountry = 'nl',
}) => {
  const { register, handleSubmit, formState, control } = useForm<HighCareFormType>({
    resolver: yupResolver(validation(translations, disableCompanyName)),
    shouldUnregister: true,
    defaultValues: defaults(formData, { companyName: '', representative: { phone: '', email: '' } }),
    mode: 'onSubmit',
  });

  return (
    <WizardLayoutFormWithCommonSection
      isSubmitting={submitting}
      backCopy={translations.back}
      onBack={eventHandlers.onBack}
      forwardCopy={translations.forward}
      onSubmit={handleSubmit(eventHandlers.onSubmit)}
    >
      {content}
      <Input
        {...register('companyName')}
        trackingId='companyName'
        id='companyName'
        error={formState.errors.companyName?.message}
        autoComplete='organization'
        disabled={submitting || disableCompanyName}
        label={translations.inputLabels.companyName}
      />
      <Controller
        name='representative.phone'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <IntlPhoneInput
            trackingId='phone'
            id='phone'
            value={field.value}
            onChange={field.onChange}
            name={field.name}
            ref={field.ref}
            error={error?.message}
            placeholder={translations.inputPlaceholders?.phonePlaceholder ?? '+31208881089'}
            label={translations.inputLabels.phone}
            defaultCountry={defaultCountry}
            preferredCountries={[defaultCountry, 'nl', 'be', 'de', 'pl', 'gb', 'hu', 'ro']}
            disabled={submitting}
          />
        )}
      />
      <Input
        {...register('representative.email')}
        trackingId='email'
        id='email'
        error={formState.errors.representative?.email?.message}
        type='email'
        autoComplete='email'
        disabled={submitting}
        label={translations.inputLabels.email}
      />
      <p className={clsx(textSm)}>{translations.termsAndConditions}</p>
    </WizardLayoutFormWithCommonSection>
  );
};
